import React, { useEffect } from "react";
import { useSimulatorStore } from "@store/simulatorStore";
import StepScene from "./StepScene";
import { useCinematic } from "./hooks/useCinematic";

export default function CinematicPlayer() {
  const state = useSimulatorStore((s) => s.state);
  const runAll = useSimulatorStore((s) => s.runAll);

  const {
    currentStep,
    currentIndex,
    next,
    prev,
    reset
  } = useCinematic();

  // Auto-run simulation once
  useEffect(() => {
    if (state.currentStep === "INIT") {
      runAll();
    }
  }, []);

  return (
    <div style={styles.container}>
      {/* 🎯 Header */}
      {currentStep !== "INIT" && (
        <div style={styles.header}>
          <img
            src="/assets/PayLens.png"
            alt="PayLens"
            style={{
              height: 60,
              objectFit: "contain"
            }}
          />
        </div>
      )}

      {/* 🎬 Scene */}
      <div style={styles.scene}>
        <StepScene step={currentStep} state={state} onStart={next} />
      </div>

      {/* 🎮 Footer Controls */}
      {currentStep !== "INIT" && currentStep !== "POS_ENTRY" && (
        <div style={styles.footerControls}>
          <button style={styles.glassButton} onClick={prev}>← Previous</button>
          <div style={styles.stepIndicator}>
            {currentIndex === 8 ? "SIMULATION ENDED" : `Step ${currentIndex}`}
          </div>
          {currentIndex < 8 && (
            <button style={styles.glassButtonPrimary} onClick={next}>Next →</button>
          )}
          <button style={styles.glassButtonDanger} onClick={reset}>Reset</button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: "relative",
    height: "100vh",
    width: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    background: "#000000"
  },
  header: {
    position: "absolute",
    top: 10,
    left: 20,
    zIndex: 20
  },
  logo: {
    fontSize: 20,
    fontWeight: 600,
    background: "linear-gradient(90deg,#E6EDF3,#3BA4F7)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },
  footerControls: {
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(12px)",
    padding: "12px 24px",
    borderRadius: 24,
    border: "1px solid rgba(255, 255, 255, 0.1)",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
    zIndex: 10
  },
  stepIndicator: {
    color: "#E6EDF3",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 1,
    textTransform: "uppercase",
    margin: "0 8px"
  },
  glassButton: {
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: "#E6EDF3",
    padding: "8px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s ease"
  },
  glassButtonPrimary: {
    background: "rgba(56, 189, 248, 0.1)",
    border: "1px solid rgba(56, 189, 248, 0.3)",
    color: "#38BDF8",
    padding: "8px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    transition: "all 0.2s ease"
  },
  glassButtonDanger: {
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    color: "#EF4444",
    padding: "8px 16px",
    borderRadius: 16,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    transition: "all 0.2s ease"
  },
  scene: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingBottom: "clamp(60px, 12vh, 100px)",
    paddingTop: "clamp(40px, 8vh, 80px)",
    minHeight: "min-content"
  }
};